import React from "react";
import { OvertimeStats } from "../types";
import {
  formatTime,
  calculateFTEVacationDays,
} from "../utils/timeFormatting";
import {
  calculateSummaries,
  calculateDailyVacationUsage,
  hasOvertimePermission,
  getMonthKey,
  getWeekKey,
  MonthlySummary,
  WeeklySummary,
} from "../utils/summaryCalculations";
import { formatDateWithWeekday } from "../utils/dateUtils";

interface OvertimeStatisticsTableProps {
  overtimeStats: OvertimeStats[];
  averageMinutesPerDay: number;
  holidays: Record<string, string>;
  isAdmin?: boolean;
  employeeUserId?: string;
  onAddPermission?: (date: string) => void;
}

const Tooltip: React.FC<{ text: string; showButton?: boolean; onButtonClick?: () => void }> = ({ text, showButton, onButtonClick }) => (
  <span
    style={{
      position: "absolute",
      bottom: "100%",
      left: "50%",
      transform: "translateX(-50%)",
      backgroundColor: "#333",
      color: "#fff",
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "0.85em",
      whiteSpace: "nowrap",
      opacity: 0,
      pointerEvents: showButton ? "auto" : "none",
      transition: "opacity 0.15s",
      marginBottom: "4px",
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      gap: "8px"
    }}
    className="custom-tooltip"
  >
    {text}
    {showButton && (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onButtonClick?.();
        }}
        style={{
          backgroundColor: "#4CAF50",
          color: "white",
          border: "none",
          borderRadius: "3px",
          padding: "2px 6px",
          cursor: "pointer",
          fontSize: "0.9em",
          fontWeight: "bold"
        }}
        title="Überstundengenehmigung hinzufügen"
      >
        +
      </button>
    )}
  </span>
);

const MonthlySummaryRow: React.FC<{
  monthKey: string;
  monthStats: MonthlySummary;
  averageMinutesPerDay: number;
}> = ({ monthKey, monthStats, averageMinutesPerDay }) => {
  return (
    <tr
      className="monthly-summary-row"
    >
      <td colSpan={3} style={{ textAlign: "left", paddingLeft: "12px" }}>
        {new Date(monthKey + "-01").toLocaleDateString("de-DE", { year: "numeric", month: "long" })}
      </td>
      <td></td>
      <td colSpan={2} style={{ position: "relative" }}>
        Urlaub genutzt: {formatTime(monthStats.vacationUsed)}
        <Tooltip text={`${calculateFTEVacationDays(monthStats.vacationUsed)} FTE`} />
      </td>
      <td colSpan={2} style={{ position: "relative" }}>
        Überstunden: {formatTime(monthStats.overtimeChange)}
        <Tooltip text={`${calculateFTEVacationDays(monthStats.overtimeChange)} FTE`} />
      </td>
    </tr>
  );
};

const WeeklySummaryRow: React.FC<{
  weekKey: string;
  weekStats: WeeklySummary;
  averageMinutesPerDay: number;
}> = ({ weekKey, weekStats, averageMinutesPerDay }) => {
  return (
    <tr
      className="weekly-summary-row"
      style={{
        fontSize: "0.95em",
      }}
    >
      <td colSpan={3} style={{ textAlign: "left", paddingLeft: "20px" }}>
        KW {parseInt(weekKey.split("-W")[1], 10)}
      </td>
      <td></td>
      <td colSpan={2} style={{ position: "relative" }}>
        Urlaub: {formatTime(weekStats.vacationUsed)}
        <Tooltip text={`${calculateFTEVacationDays(weekStats.vacationUsed)} FTE`} />
      </td>
      <td style={{ position: "relative" }}>
        Erfasst: {formatTime(weekStats.overtimeTracked)}
        <Tooltip text={`${calculateFTEVacationDays(weekStats.overtimeTracked)} FTE`} />
      </td>
      <td style={{ position: "relative" }}>
        Gewährt: {formatTime(weekStats.overtimeGranted)}
        <Tooltip text={`${calculateFTEVacationDays(weekStats.overtimeGranted)} FTE`} />
      </td>
    </tr>
  );
};

const DailyStatRow: React.FC<{
  stat: OvertimeStats;
  nextStat: OvertimeStats | null;
  averageMinutesPerDay: number;
  holidays: Record<string, string>;
  isAdmin?: boolean;
  onAddPermission?: (date: string) => void;
}> = ({ stat, nextStat, averageMinutesPerDay, holidays, isAdmin, onAddPermission }) => {
  const dailyUsedVacationMinutes = calculateDailyVacationUsage(stat, nextStat);
  const hasPermission = hasOvertimePermission(stat, nextStat);

  const statDate = new Date(stat.date);
  const dayOfWeek = statDate.getDay();
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
  const hasWorkedOnWeekend = isWeekend && stat.totalMinutesWorked > 0;
  const holidayName = holidays[stat.date];
  const isHoliday = !!holidayName;

  const isUsingVacation = dailyUsedVacationMinutes > 0;
  const isSick = stat.sickMinutes > 0;

  const getIconTooltipText = () => {
    if (isUsingVacation && isSick) return "Urlaub eingetragen & Arbeitsunfähigkeit";
    if (isUsingVacation) return "Urlaub eingetragen";
    if (isSick) return "Arbeitsunfähigkeit";
    return "";
  };

  return (
    <tr style={{ color: isWeekend ? "#999" : "inherit" }}>
      <td
        className={`${isUsingVacation ? "vacation-day" : ""} ${isSick ? "sick-day" : ""}`.trim()}
        style={{
          backgroundColor: isHoliday ? "#ecf7ee" : "transparent",
          fontWeight: isHoliday ? "bold" : "normal",
          position: "relative"
        }}
      >
        <span className="date-with-icon">
          {formatDateWithWeekday(stat.date)}
          {(isUsingVacation || isSick) && <Tooltip text={getIconTooltipText()} />}
        </span>
        {isHoliday && <Tooltip text={holidayName} />}
      </td>
      <td>{formatTime(stat.expectedMinutesWorked)}</td>
      <td style={{ color: hasWorkedOnWeekend ? "#333" : undefined }}>{formatTime(stat.totalMinutesWorked)}</td>
      <td
        style={{
          backgroundColor: stat.sickMinutes > 0 ? "#fffacd" : "transparent",
        }}
      >
        {formatTime(stat.sickMinutes)}
      </td>
      <td>
        <div>{formatTime(dailyUsedVacationMinutes)}</div>
        <small style={{ color: "#666" }}>({calculateFTEVacationDays(dailyUsedVacationMinutes)} FTE)</small>
      </td>
      <td>
        <div>{formatTime(stat.remainingVacationMinutes)}</div>
        <small style={{ color: "#666" }}>({calculateFTEVacationDays(stat.remainingVacationMinutes)} FTE)</small>
      </td>
      <td
        style={{
          backgroundColor: !hasPermission ? "#ffcc99" : stat.overOrUnderTimeMinutes < 0 ? "#ffdddd" : stat.overOrUnderTimeMinutes > 0 ? "#d4edda" : "transparent",
          color: hasWorkedOnWeekend ? "#333" : undefined,
          position: "relative"
        }}
      >
        {formatTime(stat.overOrUnderTimeMinutes)}
        <Tooltip
          text={`${calculateFTEVacationDays(stat.overOrUnderTimeMinutes)} FTE${
            !hasPermission ? " - Keine Überstundengenehmigung" : ""
          }`}
          showButton={!hasPermission && isAdmin}
          onButtonClick={() => onAddPermission?.(stat.date)}
        />
      </td>
      <td style={{ color: hasWorkedOnWeekend ? "#333" : undefined, position: "relative" }}>
        {formatTime(stat.totalOverOrUndertime)}
        <Tooltip text={`${calculateFTEVacationDays(stat.totalOverOrUndertime)} FTE`} />
      </td>
    </tr>
  );
};

const OvertimeStatisticsTable: React.FC<OvertimeStatisticsTableProps> = ({ overtimeStats, averageMinutesPerDay, holidays, isAdmin, onAddPermission }) => {
  if (overtimeStats.length === 0) {
    return <p>Keine Daten verfügbar.</p>;
  }

  const reversedStats = [...overtimeStats].reverse();
  const { monthlySummaries, weeklySummaries } = calculateSummaries(reversedStats);

  const renderRows = (): JSX.Element[] => {
    const result: JSX.Element[] = [];
    let currentMonth = "";
    let currentWeek = "";
    let isFirstRow = true;

    reversedStats.forEach((stat, index) => {
      const statDate = new Date(stat.date);
      const monthKey = getMonthKey(statDate);
      const weekKey = getWeekKey(statDate);

      if (isFirstRow) {
        // Add current month and week summaries
        result.push(
          <MonthlySummaryRow
            key={`summary-${monthKey}`}
            monthKey={monthKey}
            monthStats={monthlySummaries[monthKey]}
            averageMinutesPerDay={averageMinutesPerDay}
          />
        );
        result.push(
          <WeeklySummaryRow
            key={`week-summary-${weekKey}`}
            weekKey={weekKey}
            weekStats={weeklySummaries[weekKey]}
            averageMinutesPerDay={averageMinutesPerDay}
          />
        );
        currentMonth = monthKey;
        currentWeek = weekKey;
        isFirstRow = false;
      } else {
        // Check for period changes
        if (currentMonth !== monthKey) {
          result.push(
            <MonthlySummaryRow
              key={`summary-${monthKey}`}
              monthKey={monthKey}
              monthStats={monthlySummaries[monthKey]}
              averageMinutesPerDay={averageMinutesPerDay}
            />
          );
          currentMonth = monthKey;
        }

        if (currentWeek !== weekKey) {
          result.push(
            <WeeklySummaryRow
              key={`week-summary-${weekKey}`}
              weekKey={weekKey}
              weekStats={weeklySummaries[weekKey]}
              averageMinutesPerDay={averageMinutesPerDay}
            />
          );
          currentWeek = weekKey;
        }
      }

      // Add daily row
      const nextStat = index < reversedStats.length - 1 ? reversedStats[index + 1] : null;
      result.push(
        <DailyStatRow
          key={stat.date}
          stat={stat}
          nextStat={nextStat}
          averageMinutesPerDay={averageMinutesPerDay}
          holidays={holidays}
          isAdmin={isAdmin}
          onAddPermission={onAddPermission}
        />
      );
    });

    return result;
  };

  return (
    <div className="table-wrapper" style={{ maxHeight: "calc(100vh - 300px)", overflowY: "auto", position: "relative" }}>
      <table className="overtime-stats-table history-table">
        <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
          <tr>
            <th>Datum</th>
            <th>Erwartete Stunden</th>
            <th>Gearbeitete Stunden</th>
            <th>Krankheit</th>
            <th>Genutzter Urlaub</th>
            <th>Verbleibender Urlaub</th>
            <th>Überstunden Tag</th>
            <th>Überstunden gesamt</th>
          </tr>
        </thead>
        <tbody>{renderRows()}</tbody>
      </table>
    </div>
  );
};

export default OvertimeStatisticsTable;
